"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Search, Plus, Minus, Loader2, Users, List } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { apiFetch } from "@/lib/api/apiFetch"

interface Contact {
  _id: string
  email: string
  name: string
  status: string
}

interface List {
  _id: string
  name: string
  description: string
  contactCount: number
  contacts: Array<{ _id: string; email: string; name: string; status: string }>
}

interface ManageContactListsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedContacts: any[]
  onContactsUpdated: () => void
}

export function ManageContactListsDialog({
  open,
  onOpenChange,
  selectedContacts,
  onContactsUpdated,
}: ManageContactListsDialogProps) {
  const [lists, setLists] = useState<List[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [activeTab, setActiveTab] = useState("add")
  const [selectedLists, setSelectedLists] = useState<Set<string>>(new Set())
  const [listsToRemove, setListsToRemove] = useState<Set<string>>(new Set())
  const [updating, setUpdating] = useState(false)
  const { toast } = useToast()

  // Fetch all lists
  const fetchLists = async () => {
    try {
      setLoading(true)
      const data = await apiFetch("/api/recipient-lists?limit=1000")
      if (data.lists) {
        setLists(data.lists)
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch lists",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Load lists when dialog opens
  useEffect(() => {
    if (open) {
      fetchLists()
      setSelectedLists(new Set())
      setListsToRemove(new Set())
      setSearchTerm("")
      setActiveTab("add")
    }
  }, [open])

  // Filter lists based on search term
  const filteredLists = lists.filter(list =>
    list.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    list.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Check if a contact is in a specific list
  const isContactInList = (listId: string) => {
    return selectedContacts.some(contact =>
      lists.find(list => list._id === listId)?.contacts.some(c => c._id === contact._id)
    )
  }

  // Handle adding contacts to lists
  const handleAddToLists = async () => {
    if (selectedLists.size === 0) {
      toast({
        title: "No lists selected",
        description: "Please select at least one list to add contacts to",
        variant: "destructive",
      })
      return
    }

    try {
      setUpdating(true)

      const contactIds = selectedContacts.map(c => c._id)
      const listIds = Array.from(selectedLists)
      let totalAdded = 0
      let totalSkipped = 0

      console.log('Selected contacts:', contactIds)
      console.log('Selected lists:', listIds)

      // Add contacts to each selected list
      for (const listId of listIds) {
        const list = lists.find(l => l._id === listId)
        if (list) {
          console.log(`Processing list: ${list.name}`)
          console.log(`List current contacts:`, list.contacts)

          // Extract contact IDs from the populated contacts array
          // The API returns contacts as objects with _id, email, name, status
          const existingContactIds = Array.isArray(list.contacts)
            ? list.contacts.map(c => c._id || c.toString())
            : []

          console.log(`Existing contact IDs:`, existingContactIds)
          console.log(`Contact IDs to add:`, contactIds)

          // Filter out contacts that are already in the list to prevent duplicates
          const newContacts = contactIds.filter(id => !existingContactIds.includes(id))

          console.log(`New contacts to add:`, newContacts)
          console.log(`Contacts already in list:`, contactIds.filter(id => existingContactIds.includes(id)))

          if (newContacts.length > 0) {
            // Get the current contact IDs from the list (not the populated objects)
            const currentContactIds = Array.isArray(list.contacts)
              ? list.contacts.map(c => c._id || c.toString())
              : []

            const updatedContacts = [...currentContactIds, ...newContacts]

            await apiFetch(`/api/recipient-lists/${listId}`, {
              method: "PUT",
              data: { contacts: updatedContacts },
            })

            totalAdded += newContacts.length
            totalSkipped += (contactIds.length - newContacts.length)
          } else {
            // All contacts are already in this list
            totalSkipped += contactIds.length
          }
        }
      }

      console.log(`Total added: ${totalAdded}, Total skipped: ${totalSkipped}`)

      if (totalAdded > 0 && totalSkipped > 0) {
        toast({
          title: "Contacts added with duplicates prevented",
          description: `Added ${totalAdded} new contact(s) to ${selectedLists.size} list(s). ${totalSkipped} duplicate(s) were automatically prevented.`,
        })
      } else if (totalAdded > 0) {
        toast({
          title: "Success",
          description: `Added ${totalAdded} contact(s) to ${selectedLists.size} list(s)`,
        })
      } else {
        toast({
          title: "No new contacts added",
          description: "All selected contacts are already present in the selected lists",
          variant: "default",
        })
      }

      // Refresh the lists to get updated data
      await fetchLists()

      onContactsUpdated()
      onOpenChange(false)
    } catch (error) {
      console.error('Error adding contacts to lists:', error)
      toast({
        title: "Error",
        description: "Failed to add contacts to lists",
        variant: "destructive",
      })
    } finally {
      setUpdating(false)
    }
  }

  // Handle removing contacts from lists
  const handleRemoveFromLists = async () => {
    if (listsToRemove.size === 0) {
      toast({
        title: "No lists selected",
        description: "Please select at least one list to remove contacts from",
        variant: "destructive",
      })
      return
    }

    try {
      setUpdating(true)

      const contactIds = selectedContacts.map(c => c._id)
      const listIds = Array.from(listsToRemove)

      // Remove contacts from each selected list
      for (const listId of listIds) {
        const list = lists.find(l => l._id === listId)
        if (list) {
          const updatedContacts = list.contacts
            .map(c => c._id)
            .filter(id => !contactIds.includes(id))

          await apiFetch(`/api/recipient-lists/${listId}`, {
            method: "PUT",
            data: { contacts: updatedContacts },
          })
        }
      }

      toast({
        title: "Success",
        description: `Removed ${selectedContacts.length} contact(s) from ${listsToRemove.size} list(s)`,
      })

      onContactsUpdated()
      onOpenChange(false)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove contacts from lists",
        variant: "destructive",
      })
    } finally {
      setUpdating(false)
    }
  }

  const handleSelectList = (listId: string, checked: boolean) => {
    if (activeTab === "add") {
      if (checked) {
        setSelectedLists(prev => new Set(prev).add(listId))
      } else {
        setSelectedLists(prev => {
          const newSet = new Set(prev)
          newSet.delete(listId)
          return newSet
        })
      }
    } else {
      if (checked) {
        setListsToRemove(prev => new Set(prev).add(listId))
      } else {
        setListsToRemove(prev => {
          const newSet = new Set(prev)
          newSet.delete(listId)
          return newSet
        })
      }
    }
  }

  const handleSelectAllLists = (checked: boolean) => {
    if (activeTab === "add") {
      if (checked) {
        setSelectedLists(new Set(filteredLists.map(l => l._id)))
      } else {
        setSelectedLists(new Set())
      }
    } else {
      if (checked) {
        setListsToRemove(new Set(filteredLists.map(l => l._id)))
      } else {
        setListsToRemove(new Set())
      }
    }
  }

  const areAllContactsInList = (listId: string) => {
    const list = lists.find(l => l._id === listId);
    if (!list) return false;

    return selectedContacts.every(contact =>
      list.contacts.some(c => c._id === contact._id)
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Manage Contact Lists</DialogTitle>
          <DialogDescription>
            {selectedContacts.length} contact(s) selected: {selectedContacts.map(c => c.email).join(", ")}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="add" className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add to Lists
            </TabsTrigger>
            <TabsTrigger value="remove" className="flex items-center gap-2">
              <Minus className="h-4 w-4" />
              Remove from Lists
            </TabsTrigger>
          </TabsList>

          <TabsContent value="add" className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search lists..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                onClick={handleAddToLists}
                disabled={selectedLists.size === 0 || updating}
                className="min-w-[120px]"
              >
                {updating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Add to {selectedLists.size} List(s)
                  </>
                )}
              </Button>
            </div>

            <div className="border rounded-lg">
              <div className="p-3 border-b bg-gray-50">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedLists.size === filteredLists.length && filteredLists.length > 0}
                    onCheckedChange={handleSelectAllLists}
                  />
                  <span className="font-medium">Select Lists</span>
                  <Badge variant="secondary">{selectedLists.size} selected</Badge>
                </div>
              </div>

              <ScrollArea className="h-64">
                {loading ? (
                  <div className="p-8 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Loading lists...
                  </div>
                ) : filteredLists.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No lists found
                  </div>
                ) : (
                  <div className="p-2 space-y-2">
                    {filteredLists.map((list) => (
                      <div key={list._id} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                        <Checkbox
                          checked={selectedLists.has(list._id)}
                          onCheckedChange={(checked) => handleSelectList(list._id, checked as boolean)}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{list.name}</div>
                          <div className="text-sm text-gray-600">{list.description}</div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Users className="h-4 w-4" />
                          {list.contactCount}
                          {isContactInList(list._id) && (
                            <Badge variant="outline" className="text-xs">
                              Already in list
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="remove" className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search lists..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                onClick={handleRemoveFromLists}
                disabled={listsToRemove.size === 0 || updating}
                variant="destructive"
                className="min-w-[120px]"
              >
                {updating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    <Minus className="h-4 w-4 mr-2" />
                    Remove from {listsToRemove.size} List(s)
                  </>
                )}
              </Button>
            </div>

            <div className="border rounded-lg">
              <div className="p-3 border-b bg-gray-50">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={listsToRemove.size === filteredLists.length && filteredLists.length > 0}
                    onCheckedChange={handleSelectAllLists}
                  />
                  <span className="font-medium">Select Lists</span>
                  <Badge variant="secondary">{listsToRemove.size} selected</Badge>
                </div>
              </div>

              <ScrollArea className="h-64">
                {loading ? (
                  <div className="p-8 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Loading lists...
                  </div>
                ) : filteredLists.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No lists found
                  </div>
                ) : (
                  <div className="p-2 space-y-2">
                    {filteredLists
                      .filter(list => areAllContactsInList(list._id))
                      .map((list) => (
                        <div key={list._id} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                          <Checkbox
                            checked={listsToRemove.has(list._id)}
                            onCheckedChange={(checked) => handleSelectList(list._id, checked as boolean)}
                          />
                          <div className="flex-1">
                            <div className="font-medium">{list.name}</div>
                            <div className="text-sm text-gray-600">{list.description}</div>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Users className="h-4 w-4" />
                            {list.contactCount}
                            <Badge variant="outline" className="text-xs text-green-600">
                              Contact in list
                            </Badge>
                          </div>
                        </div>
                      ))}
                    {filteredLists.filter(list => areAllContactsInList(list._id)).length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        <List className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No lists contain the selected contacts</p>
                        <p className="text-sm">Switch to "Add to Lists" to add them to lists</p>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
